import {copilot, users} from '@cl/shared';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import CLLocalStorage from 'common/src/modules/localStorage/CLLocalStorage';
import {CLDispatch, CLStore} from 'common/src/redux/store';
import {ChatState} from '../../copilot.types';
import {addChat, setActiveChatId} from '../../store/copilot.slice';
import {PersistedChatsData, PersistedChatSnapshot} from '../persistence.types';
import getCopilotChatsLSKey from './getCopilotChatsLSKey';
import convertSnapshotToChat from './typeConverters/convertSnapshotToChat';
import validateSnapshot from './validateSnapshot';

export default function restoreChatsFromLocalStorage(store: CLStore): boolean {
  const {dispatch, getState} = store;

  if (
    !isFlagEnabled(FeatureFlagKey.EnableCopilotSkills) ||
    !isFlagEnabled(FeatureFlagKey.EnableCopilotMultiChat)
  ) {
    return false;
  }

  const userId = users.userSubject.data?.id;

  if (!userId) {
    return false;
  }

  if (copilot.allChats.current.length > 0 || getState().copilot.chats.length > 0) {
    return false;
  }

  const persisted = CLLocalStorage.getParsed<PersistedChatsData>(getCopilotChatsLSKey(userId));

  if (!Array.isArray(persisted?.snapshots) || persisted.snapshots.length === 0) {
    return false;
  }

  const restored = persisted.snapshots.flatMap((snapshot) => {
    const result = _restoreSnapshot(snapshot, dispatch);

    if (result) {
      return [result];
    }

    return [];
  });

  if (restored.length === 0) {
    return false;
  }

  const restoredActiveChat = restored.find(
    (restoredChat) => restoredChat.id === persisted.activeChatId,
  );
  const activeChatId = restoredActiveChat ? persisted.activeChatId : restored[0].id;

  if (activeChatId) {
    dispatch(setActiveChatId(activeChatId));
  }

  return true;
}

function _restoreSnapshot(snapshot: PersistedChatSnapshot, dispatch: CLDispatch): ChatState | null {
  const validationResult = validateSnapshot(snapshot);

  if (!validationResult.success) {
    return null;
  }

  try {
    const {restoredRuntimeChat, restoredReduxState} = convertSnapshotToChat(validationResult.data, {
      preserveChatIds: true,
    });
    copilot.addChat(restoredRuntimeChat);
    dispatch(addChat({chat: restoredReduxState, setAsActive: false}));

    return restoredReduxState;
  } catch (err) {
    console.error('[copilot] Failed to restore snapshot', err);

    return null;
  }
}
